// This component is duplicated in the other BodyTalk storefront (bodytalk-front /
// bodytalk-b2b-front); keep both copies in sync.
import PropTypes from 'prop-types';
import { useRef } from 'react';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const DEFAULT_COUNTRY = 'gr';

// An explicitly international number already carries its own dial code, so its
// leading digits must not be treated as a national number and prefixed again.
const INTERNATIONAL_PREFIX = /^\s*(\+|00)/;

/**
 * react-phone-input-2 reports the number as a single digit string in which the dial
 * code is not separable from the national part, so re-attach the dial code unless
 * the digits already start with it. Returns the canonical `+<dialCode><national>`
 * form, or an empty string for an empty field.
 */
export const toStoredNumber = (digits, dialCode, isInternational = false) => {
	if (!digits) return '';

	// A leading 00 is the written form of "+", not part of the number.
	const national = isInternational ? digits.replace(/^00/, '') : digits;

	if (isInternational || !dialCode || national.startsWith(dialCode)) return `+${national}`;
	return `+${dialCode}${national}`;
};

/**
 * Phone input hardened against react-phone-input-2's `countryCodeEditable={false}`
 * behaviour: that prop makes the library silently discard any edit whose result does
 * not literally start with `+<dialCode>`, which is what broke pasting, select-all +
 * replace, clearing the field, and typing with the caret before the prefix (BA-6735).
 * The dial code is re-applied on every change instead, so no input is ever dropped.
 *
 * `onChange` receives the full number as `+<dialCode><national digits>`, or an empty
 * string when the field is cleared. Display formatting is left to the call site
 * (`autoFormat`, `defaultMask`, ...); only the stored value is normalised. Every prop not
 * named above is forwarded to `react-phone-input-2` untouched, and `onChange` is optional so
 * a read-only usage can render the field without a handler.
 */
const PhoneInput = ({ value, onChange = () => {}, country = DEFAULT_COUNTRY, inputProps, ...rest }) => {
	// Set on paste and read by the onChange the library fires immediately after it.
	const pastedInternational = useRef(false);

	const handlePaste = e => {
		pastedInternational.current = INTERNATIONAL_PREFIX.test(e.clipboardData?.getData('text') ?? '');
		inputProps?.onPaste?.(e);
	};

	const handleChange = (digits, countryData) => {
		const dialCode = countryData?.dialCode;

		// The paste flag only covers the edit that pasted. Because the country stays pinned,
		// later edits have to keep treating an already-foreign number as international by
		// re-reading what we stored, or the next keystroke re-prefixes it with the local
		// dial code and corrupts it.
		const isInternational = pastedInternational.current || (!!value && !!dialCode && !value.startsWith(`+${dialCode}`));

		pastedInternational.current = false;
		onChange(toStoredNumber(digits, dialCode, isInternational));
	};

	return (
		<ReactPhoneInput
			{...rest}
			country={country}
			value={value}
			// Never `false`: that is the prop that makes the library drop non-conforming
			// input outright instead of correcting it. handleChange re-applies the dial code.
			countryCodeEditable
			// Stop the library re-guessing the country from the digits while typing, so a pasted
			// Greek mobile such as 6912345678 is not read as the +691 dial code. The initial
			// guess from `value` still runs, which is what makes editing a stored foreign
			// number work.
			disableCountryGuess
			inputProps={{ ...inputProps, onPaste: handlePaste }}
			onChange={handleChange}
		/>
	);
};

PhoneInput.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func,
	country: PropTypes.string,
	inputProps: PropTypes.object
};

export default PhoneInput;
